import React, { useEffect, useState } from 'react'
import { Icon } from 'antd'
import { AiScore, pct } from './AiBasis'

/**
 * JevCheck — the AI search panel in the PV link picker (the AI service is Jev,
 * TypeSafe; the UI says "AI").
 *
 * The container walks the link options in priority order (most specific rule group
 * first, assets in listed order) asking the AI whether the PV item is the same
 * physical item, and stops at the first option at or above the threshold, or after
 * the cap of new AI checks. This panel shows where the search is (running · match ·
 * none reached the threshold · cap reached · stopped · paused on an error). Scores
 * are shown on the checked cards (and the match here); clicking one shows what the
 * AI used to decide. The search runs in AI link mode, not when the picker opens.
 * A failed check shows no score - never 0%. Nothing is skipped silently.
 * Design: docs/reconciliation-cockpit-pv-jev-check.md
 */

const Elapsed = ({ since }) => {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 1000)
    return () => clearInterval(t)
  }, [])
  return <span className="tnum">{Math.max(0, Math.round((Date.now() - since) / 1000))}s</span>
}

const scoreOf = (c) => (c && c.doc && c.doc.status === 'OK' && typeof c.doc.sameProbability === 'number') ? c.doc.sameProbability : null

const JevCheck = ({ search, threshold, checkOf, available, onStop, onContinue, onRestart }) => {
  if (!search) return null
  const order = search.order || []
  const total = order.length
  // Rows: every option up to and including the current position that has a result.
  const rows = order.slice(0, Math.min(total, (search.pos || 0) + 1))
    .map((o, i) => ({ ...o, n: i + 1, check: checkOf(o.asset) }))
    .filter(r => r.check)
  const cur = order[search.pos] || null
  const match = search.matchId ? order.find(o => String(o.asset._id) === search.matchId) : null
  const matchCheck = match ? checkOf(match.asset) : null
  const checkedCount = rows.filter(r => scoreOf(r.check) !== null).length

  let status = null
  if (search.status === 'running') {
    status = <span className="jv-run"><Icon type="loading" /> Checking option {search.pos + 1} of {total}
      {cur ? <> — <b>{cur.asset.assetCode}</b> ({cur.groupName})</> : null}… <Elapsed since={search.startedAt} />
      <a onClick={onStop}>Stop</a></span>
  } else if (search.status === 'found' && matchCheck && scoreOf(matchCheck) !== null) {
    const gone = available && !available.has(search.matchId)
    const idn = search.identical || {}
    status = <span>
      <span className="jv-match">Match: <b>{match.asset.assetCode}</b></span>{' '}
      <AiScore prob={scoreOf(matchCheck)} assetCode={match.asset.assetCode} state={matchCheck.doc.request && matchCheck.doc.request.state} threshold={threshold} />
      {' '}option {search.pos + 1} of {total} ({match.groupName}){gone ? '' : ' — highlighted below'}
      {gone && <div className="jv-err">This asset can no longer be linked (taken by another decision). <a onClick={onContinue}>Continue the search</a></div>}
      {idn.checking && <div className="jv-run"><Icon type="loading" /> Checking whether other options look identical to the AI…</div>}
      {idn.codes && idn.codes.length > 0 && (
        <div className="jv-amb">Ambiguous: the AI would receive exactly the same information for {idn.codes.length} other
          option{idn.codes.length === 1 ? '' : 's'} ({idn.codes.join(', ')}) and give the same score. It cannot tell
          them apart — compare the photos before linking.</div>
      )}
      {idn.codes && idn.codes.length === 0 && !idn.partial && idn.checked > 0 && (
        <div className="jv-meta">{idn.checked} similar option{idn.checked === 1 ? '' : 's'} checked: none would look identical to the AI.</div>
      )}
      {idn.partial && <div className="jv-err">Identical-option check incomplete{idn.error ? ` — ${idn.error}` : ''}.</div>}
    </span>
  } else if (search.status === 'capped') {
    status = <span>{search.cap} new AI checks used without a match — option {search.pos} of {total} reached.
      {' '}<a onClick={onContinue}>Continue (next {search.cap})</a></span>
  } else if (search.status === 'exhausted') {
    status = <span className="jv-err">No option reached {pct(threshold)} — all {total} checked.</span>
  } else if (search.status === 'stopped') {
    status = <span>Stopped at option {Math.min(search.pos + 1, total)} of {total}. <a onClick={onContinue}>Continue</a></span>
  } else if (search.status === 'error') {
    status = <span className="jv-err">Paused at option {search.pos + 1} of {total}
      {cur ? ` (${cur.asset.assetCode})` : ''}: {search.error} · <a onClick={onContinue}>Continue from here</a></span>
  }

  return (
    <div className="jv">
      <div className="jv-row">
        <span className="jv-title">AI search · priority order · stops at ≥ {pct(threshold)}</span>
        {status}
        <span className="jv-meta">{checkedCount} checked</span>
        {search.status !== 'running' && <a className="jv-toggle" onClick={onRestart}>Search again</a>}
      </div>
    </div>
  )
}

export default JevCheck
